import { P2GIcon, P2GText, P2GTooltip, SvgConfirmCircle, SvgFillWarningTriangle } from '@parcel2go/core-ui';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DropLocation, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { TranslationNamespaces } from '~/lib/constants';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { convertMetresToMiles } from '~/lib/utils/convert-metres-to-miles';

interface props {
  item: SmartSendItemApiModel;
  wrapper: (c: React.ReactElement) => React.ReactElement;
}

interface innerprops {
  provider: string;
  accountId: string;
  locationProvider: string;
  country: string;
  location: string;
  serviceTags: string[];
  wrapper: (c: React.ReactElement) => React.ReactElement;
}

export const ShopProximity: React.FC<props> = (props) => {
  if (
    !SettingsCache.settings.Features.includes('ShopProximityMessage') ||
    !props.item?.State?.validation?.QuoteState?.Quote?.Service?.DropOffProviderCode ||
    !props.item?.Sender?.CountryIsoCode
  ) {
    return null;
  }
  return (
    <ShopProximityInner
      provider={props.item.Service.Provider}
      accountId={props.item.Service.AccountId}
      locationProvider={props.item.State.validation.QuoteState.Quote.Service.DropOffProviderCode}
      country={props.item.Sender.CountryIsoCode}
      location={props.item.Sender.Postcode}
      serviceTags={props.item.State.validation.QuoteState.Quote.Service.Tags}
      wrapper={props.wrapper}
    />
  );
};

export const ShopProximityInner: React.FC<innerprops> = (props) => {
  const [shopResult, setShopResult] = useState<{ shop: DropLocation; isGt: boolean; distance: number }>();
  const { getDropLocations } = useSmartSendApi();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const getDropShops = async () => {
    // Check if the item has a drop off provider code
    if (
      !SettingsCache.settings.Features.includes('ShopProximityMessage') ||
      !props.locationProvider ||
      !props.country
    ) {
      return;
    }

    // Check if the item has a proximity warning tag eg:
    // 'ShopProximityMessage>100' - if the closest shop is further than 100m - Warning message
    // 'ShopProximityMessage<100' - if the closest shop is closer than 100m - Positive message
    const tag = props.serviceTags.find((x) => x.startsWith('ShopProximityMessage'));
    if (!tag || (!tag.includes('<') && !tag.includes('>'))) {
      return;
    }

    const isGt = tag.includes('>');
    const distance = parseInt(tag.split(isGt ? '>' : '<')[1]);

    const res = await getDropLocations(
      props.provider,
      props.accountId,
      props.locationProvider,
      props.country,
      props.location
    );

    // If its gt, we want the first shop that is further away than the distance,
    // otherwise we want the first shop that is closer than the distance
    if (res.success && res.result?.DropLocations?.length) {
      const s = res.result.DropLocations.filter((x) => (isGt ? x.Distance > distance : x.Distance < distance))[0];
      if (s) {
        setShopResult({ shop: s, isGt: isGt, distance: distance });
      }
    }
  };

  useEffect(() => {
    getDropShops();
  }, []);

  if (!shopResult?.shop) {
    return null;
  }

  return props.wrapper?.(
    <P2GTooltip title={`${shopResult.shop.Name}, ${shopResult.shop.Address1}`} placement="top">
      <P2GText variant="body2" display="flex" alignItems="center" gap={1}>
        {shopResult.isGt ? (
          <>
            <P2GIcon palette="warning" size="xs">
              <SvgFillWarningTriangle />
            </P2GIcon>
            <span>
              <Trans
                i18nKey="SHOP_PROXIMITY_WARNING"
                t={t}
                values={{ distance: convertMetresToMiles(shopResult.shop.Distance).toFixed(2) }}
                defaults="Your closest drop shop is <strong>{{distance}} miles</strong> away"
              />
            </span>
          </>
        ) : (
          <>
            <P2GIcon palette="success" size="xs">
              <SvgConfirmCircle />
            </P2GIcon>
            <span>
              <Trans
                i18nKey="SHOP_PROXIMITY_CONFIRM_INFO"
                t={t}
                values={{ distance: convertMetresToMiles(shopResult.shop.Distance).toFixed(2) }}
                defaults="There's a drop shop within <strong>{{distance}} miles</strong>"
              />
            </span>
          </>
        )}
      </P2GText>
    </P2GTooltip>
  );
};
